import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Divider,
} from '@mui/material';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { ContactFormFields } from '@sophocles/components/contacts';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { createContact, updateContact } from '@sophocles/features/contacts/api';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';

// Form field names, used to attribute API validation errors to their input;
// anything the backend reports outside this list (e.g. the internal
// customer/site_group ownership fields) downgrades to the banner.
const CONTACT_FIELDS = [
  'name',
  'email',
  'business_name',
  'phone1',
  'address',
  'address_2',
  'city',
  'state',
  'zipcode',
  'country',
  'is_primary_rma_billing',
  'is_primary_rma_shipping',
  'is_primary_sales_billing',
  'is_primary_sales_shipping',
];

/**
 * ContactForm - Complete contact form with dialog wrapper
 *
 * @component
 * @example
 * // Create mode in dialog
 * <ContactForm
 *   open={dialogOpen}
 *   onClose={handleClose}
 *   customerId={customerId}
 *   siteGroupId={siteGroupId}
 * />
 *
 * @example
 * // Edit mode in dialog
 * <ContactForm
 *   open={dialogOpen}
 *   onClose={handleClose}
 *   contact={selectedContact}
 *   customerId={customerId}
 * />
 *
 * @example
 * // Inline mode for embedding in forms
 * <ContactForm
 *   mode="inline"
 *   open={true}
 *   onClose={handleCancel}
 *   customerId={customerId}
 *   onSuccess={(contact) => {
 *     // Populate parent form fields
 *     formik.setFieldValue('ship_contact', contact.name);
 *   }}
 * />
 */
const ContactForm = ({
  open,
  onClose,
  contact = null,
  customerId = null, // Customer ID - optional for use-once scenarios
  siteGroupId = null,
  onSuccess = null,
  mode = 'dialog',
  showBackButton = false, // Show "Back to Address Book" button
  onBackToAddressBook = null, // Callback for back button
  isManualEntry = false, // Flag for manual entry mode
  customerName = 'Customer', // Customer name for save location selector
  siteGroupName = 'Site Group', // Site Group name for save location selector
  standaloneMode = false, // When true, show all action buttons and handle logic internally
  allowUseOnce = false, // When true, show "Use Once" button in non-standalone modes (for AddressBookDialog context)
  savedContactsOnly = false, // When true, contacts must be saved to address book (hide "Use for this order only" option)
  requiredFields = [], // Array of required fields: 'email', 'name', 'address', 'phone'. Empty array = all optional.
  primaryContext = 'none', // Context for primary options: 'all', 'rma', 'sales', or 'none'
  addressOnlyMode = false, // When true, hide save options and only allow "Use for this order only"
  validateIfAnyFilled = false, // When true, if ANY field is filled, require name/businessName, email, and full address
  // Deprecated props - kept for backwards compatibility but derived internally
  hasExistingContact: hasExistingContactProp,
  useOnceOnly: useOnceOnlyProp,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Derive noRequiredFields from empty requiredFields array for backwards compatibility
  const noRequiredFields = requiredFields.length === 0;

  // Derive hasExistingContact internally - use prop if provided for backwards compat, otherwise derive
  // A "real" saved contact has an ID and isn't an address record or manual entry
  const hasExistingContact =
    hasExistingContactProp !== undefined
      ? hasExistingContactProp
      : !!contact?.id && !contact?._isAddress && !contact?._isManualEntry;

  // Derive useOnceOnly internally - use prop if provided for backwards compat, otherwise derive
  // useOnceOnly=true means ONLY show "Use Once" button (no save options)
  // This applies when EDITING an existing inline entry (address record or manual entry without FK)
  // For NEW contacts (contact is null), we want to show full options (save as new OR use once)
  const useOnceOnly =
    useOnceOnlyProp !== undefined
      ? useOnceOnlyProp
      : !!contact && (!contact.id || contact._isAddress || contact._isManualEntry);

  // Addresses have synthetic IDs but should be treated as new contacts
  // Synthetic contacts (from "Use Once") also don't have IDs but should be editable
  const isEditMode = !!contact && (!!contact.id || !!contact._isSynthetic) && !contact._isAddress;
  const [saveLevel, setSaveLevel] = React.useState('site_group'); // 'customer' or 'site_group'

  // Save mode state - controls whether primary options are shown
  // 'useOnce' = use for this form only, don't save to address book (hide primary options)
  // 'save' = save to address book (show primary options)
  // When savedContactsOnly is true, always default to 'save' mode
  const initialSaveMode = savedContactsOnly
    ? 'save'
    : addressOnlyMode
      ? 'useOnce'
      : hasExistingContact
        ? 'save'
        : 'useOnce';
  const [saveMode, setSaveMode] = React.useState(initialSaveMode);
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  // Update saveMode when dialog reopens
  React.useEffect(() => {
    if (standaloneMode) {
      const newSaveMode = savedContactsOnly
        ? 'save'
        : addressOnlyMode
          ? 'useOnce'
          : hasExistingContact
            ? 'save'
            : 'useOnce';
      setSaveMode(newSaveMode);
    }
  }, [open, hasExistingContact, standaloneMode, addressOnlyMode, savedContactsOnly]);

  React.useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const initialValues = useMemo(
    () => {
      const values = {
        name: contact?.name || '',
        email: contact?.email || '',
        business_name: contact?.business_name || '',
        phone1: contact?.phone1 || '',
        address: contact?.address || '',
        address_2: contact?.address_2 || '',
        city: contact?.city || '',
        state: contact?.state || '',
        zipcode: contact?.zipcode || '',
        country: contact?.country || '',
        is_primary_rma_billing: contact?.is_primary_rma_billing || false,
        is_primary_rma_shipping: contact?.is_primary_rma_shipping || false,
        is_primary_sales_billing: contact?.is_primary_sales_billing || false,
        is_primary_sales_shipping: contact?.is_primary_sales_shipping || false,
      };
      return values;
    },
    // Include all contact fields that affect initial values
    [
      contact?.id,
      contact?.name,
      contact?.email,
      contact?.business_name,
      contact?.phone1,
      contact?.address,
      contact?.address_2,
      contact?.city,
      contact?.state,
      contact?.zipcode,
      contact?.country,
      contact?.is_primary_rma_billing,
      contact?.is_primary_rma_shipping,
      contact?.is_primary_sales_billing,
      contact?.is_primary_sales_shipping,
    ],
  );

  const validationSchema = useMemo(() => {
    const baseSchema = ContactFormFields.getValidationSchema(t, {
      fieldPrefix: '',
      requiredAddress: requiredFields.includes('address'),
      showBusinessName: true,
      primaryContext, // Pass context for validation schema
      requiredContact: false, // Don't require email/phone for RMA shipping/billing
      requiredFields, // Pass full array for granular control
      addressOnlyMode, // Skip name validation when only address fields are shown
      noRequiredFields: noRequiredFields && !validateIfAnyFilled, // When validateIfAnyFilled, don't bypass validation
    });

    // Helper to check if any contact field has a value (for validateIfAnyFilled mode)
    const hasAnyContactField = function () {
      const allFields = [
        'name',
        'business_name',
        'email',
        'phone1',
        'address',
        'address_2',
        'city',
        'state',
        'zipcode',
        'country',
      ];
      return allFields.some(field => {
        const value = this.parent[field];
        return value && value.trim();
      });
    };

    // Helper to check if any address field has a value
    const hasAnyAddressField = function () {
      const addressFields = ['address', 'city', 'state', 'zipcode', 'country'];
      return addressFields.some(field => {
        const value = this.parent[field];
        return value && value.trim();
      });
    };

    // Address fields are required if:
    // 1. validateIfAnyFilled is true AND any contact field has a value, OR
    // 2. noRequiredFields is false AND:
    //    a. addressOnlyMode is true (the whole point is to enter an address), OR
    //    b. Any primary is set (RMA or Sales billing/shipping), OR
    //    c. Any other address field has a value (partial address validation)
    const isAddressRequired = function () {
      if (validateIfAnyFilled && hasAnyContactField.call(this)) return true;
      if (noRequiredFields) return false; // No required fields mode - everything is optional
      if (addressOnlyMode) return true;
      const { is_primary_rma_billing, is_primary_rma_shipping, is_primary_sales_billing, is_primary_sales_shipping } =
        this.parent;
      return (
        is_primary_rma_billing ||
        is_primary_rma_shipping ||
        is_primary_sales_billing ||
        is_primary_sales_shipping ||
        hasAnyAddressField.call(this)
      );
    };

    // Name/email required if validateIfAnyFilled AND any field has a value
    const isNameEmailRequired = function () {
      if (validateIfAnyFilled && hasAnyContactField.call(this)) return true;
      return false;
    };

    return baseSchema.shape({
      // Name validation - required if validateIfAnyFilled and any field filled
      name: Yup.string()
        .max(200, t('validation.maxLength', 'Maximum {{max}} characters', { max: 200 }))
        .test(
          'name-or-business-required',
          t('validation.nameOrBusinessRequired', 'Name or Business Name is required'),
          function (value) {
            if (isNameEmailRequired.call(this)) {
              const businessName = this.parent.business_name;
              return !!(value?.trim() || businessName?.trim());
            }
            return true;
          },
        ),
      business_name: Yup.string()
        .max(200, t('validation.maxLength', 'Maximum {{max}} characters', { max: 200 }))
        .test(
          'business-or-name-required',
          t('validation.nameOrBusinessRequired', 'Name or Business Name is required'),
          function (value) {
            if (isNameEmailRequired.call(this)) {
              const name = this.parent.name;
              return !!(value?.trim() || name?.trim());
            }
            return true;
          },
        ),
      // Email validation - required if validateIfAnyFilled and any field filled
      email: Yup.string()
        .email(t('validation.invalidEmail', 'Invalid email address'))
        .max(254, t('validation.maxLength', 'Maximum {{max}} characters', { max: 254 }))
        .test('email-required', t('validation.required', 'Email is required'), function (value) {
          if (isNameEmailRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
      address: Yup.string()
        .max(500, t('validation.maxLength', 'Maximum {{max}} characters', { max: 500 }))
        .test('address-required', t('validation.required', 'This field is required'), function (value) {
          if (isAddressRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
      city: Yup.string()
        .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }))
        .test('city-required', t('validation.required', 'This field is required'), function (value) {
          if (isAddressRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
      state: Yup.string()
        .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }))
        .test('state-required', t('validation.required', 'This field is required'), function (value) {
          if (isAddressRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
      zipcode: Yup.string()
        .max(20, t('validation.maxLength', 'Maximum {{max}} characters', { max: 20 }))
        .matches(
          /^[A-Z0-9\s-]*$/,
          t('validation.postalCodeFormat', 'Postal code must contain only letters, numbers, spaces, and hyphens'),
        )
        .test('zipcode-required', t('validation.required', 'This field is required'), function (value) {
          if (isAddressRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
      country: Yup.string()
        .max(100, t('validation.maxLength', 'Maximum {{max}} characters', { max: 100 }))
        .test('country-required', t('validation.required', 'This field is required'), function (value) {
          if (isAddressRequired.call(this)) {
            return !!(value && value.trim());
          }
          return true;
        }),
    });
  }, [requiredFields, primaryContext, addressOnlyMode, noRequiredFields, validateIfAnyFilled]);

  const handleSubmit = async (values, { setSubmitting, setErrors }, action = 'save') => {
    setNonFieldMessages([]);
    try {
      const payload = {
        ...values,
      };

      if (primaryContext === 'rma') {
        delete payload.is_primary_sales_billing;
        delete payload.is_primary_sales_shipping;
      } else if (primaryContext === 'sales') {
        delete payload.is_primary_rma_billing;
        delete payload.is_primary_rma_shipping;
      } else if (primaryContext === 'none') {
        delete payload.is_primary_rma_billing;
        delete payload.is_primary_rma_shipping;
        delete payload.is_primary_sales_billing;
        delete payload.is_primary_sales_shipping;
      }

      // Set customer OR site_group based on saveLevel (mutually exclusive per backend model)
      // In standalone mode, default to site_group if available, otherwise customer
      if (action === 'save' || action === 'saveAsNew') {
        // Determine effective save level: use saveLevel if in non-standalone mode,
        // otherwise prefer site_group if available
        const effectiveSaveLevel = standaloneMode ? (siteGroupId ? 'site_group' : 'customer') : saveLevel;

        if (effectiveSaveLevel === 'site_group' && siteGroupId) {
          // Site group contact - only set site_group
          payload.site_group = parseInt(siteGroupId, 10);
          payload.customer = null;
        } else if (customerId) {
          // Customer contact - only set customer
          payload.customer = parseInt(customerId, 10);
          payload.site_group = null;
        }
      } else {
        // For other actions (like update), preserve existing ownership - prefer site_group
        if (siteGroupId) {
          payload.site_group = parseInt(siteGroupId, 10);
          payload.customer = null;
        } else if (customerId) {
          payload.customer = parseInt(customerId, 10);
          payload.site_group = null;
        }
      }

      let result;

      if (action === 'useOnce') {
        // Use once without saving - create synthetic contact
        result = {
          ...values,
          id: null,
          _isSynthetic: true,
          _isManualEntry: true,
          full_address_display: `${values.address}${values.address_2 ? ', ' + values.address_2 : ''}, ${values.city}, ${values.state} ${values.zipcode}`,
        };

        if (onSuccess) {
          onSuccess(result);
        }

        onClose();
        return;
      }

      if (action === 'update') {
        // Update existing contact (dispatch thunk to update Redux store)
        result = await dispatch(updateContact({ id: contact.id, ...payload })).unwrap();
        dispatch(
          addToast({
            message: t('contacts.management.updateSuccess', 'Contact updated successfully'),
            level: 'success',
          }),
        );
      } else if (action === 'saveAsNew') {
        // Save as new contact (remove ID)
        delete payload.id;
        delete payload._isSynthetic;
        delete payload._isManualEntry;
        result = await dispatch(createContact(payload)).unwrap();
        dispatch(
          addToast({
            message: t('contacts.management.createSuccess', 'Contact created successfully'),
            level: 'success',
          }),
        );
      } else {
        // Default 'save' action - create new contact
        delete payload.id;
        delete payload._isSynthetic;
        delete payload._isManualEntry;
        result = await dispatch(createContact(payload)).unwrap();
        dispatch(
          addToast({
            message: t('contacts.management.createSuccess', 'Contact created successfully'),
            level: 'success',
          }),
        );
      }

      if (onSuccess) {
        onSuccess(result);
      }

      onClose();
    } catch (error) {
      setSubmitting(false);
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors,
          knownFields: CONTACT_FIELDS,
          genericMessage: t('contacts.management.saveError', 'Failed to save contact'),
        }),
      );
    }
  };

  const handleSubmitWithValidation = async (values, formikProps, action = 'save') => {
    // Run validation explicitly before submission to catch stale isValid state
    // from dynamic .test() validators that depend on sibling field values.
    const validationErrors = await formikProps.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      formikProps.setTouched(Object.keys(validationErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      return;
    }
    handleSubmit(values, formikProps, action);
  };

  const renderFormContent = formikProps => {
    const hasCompleteAddress = !!(
      formikProps.values.address &&
      formikProps.values.city &&
      formikProps.values.state &&
      formikProps.values.zipcode
    );

    // Determine primaryContext for ContactFormFields:
    // - In standaloneMode with useOnce selected, always hide primary options
    // - Otherwise use the prop value if it's not 'none'
    // - If 'none' but in save mode, has complete address, allow showing primary options
    // - For manual entry or addressOnlyMode, hide primary options
    const shouldHidePrimary = standaloneMode && saveMode === 'useOnce';
    const effectivePrimaryContext = shouldHidePrimary
      ? 'none'
      : primaryContext !== 'none'
        ? primaryContext
        : !isManualEntry && !addressOnlyMode && hasCompleteAddress
          ? 'all'
          : 'none';

    return (
      <Grid container spacing={2.5}>
        <ContactFormFields
          fieldPrefix=""
          primaryContext={effectivePrimaryContext}
          showBusinessName={true}
          requiredAddress={requiredFields.includes('address')}
          requiredFields={requiredFields}
          gridColumns={{ xs: 12, sm: 6 }}
          addressOnlyMode={addressOnlyMode}
          validateIfAnyFilled={validateIfAnyFilled}
        />
      </Grid>
    );
  };

  if (mode === 'dialog') {
    return (
      <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
        <DialogTitle>
          {addressOnlyMode
            ? isEditMode
              ? t('contacts.management.editAddress', 'Edit Address')
              : t('contacts.management.enterAddress', 'Enter Address')
            : isEditMode
              ? t('contacts.management.editTitle', 'Edit Contact')
              : isManualEntry
                ? t('contacts.management.newContact', 'New Contact')
                : t('contacts.management.createTitle', 'Add Contact')}
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount={true}
          enableReinitialize={true}
        >
          {formikProps => (
            <Form>
              <DialogContent dividers>
                <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
                {/* Mode Toggle - Only in standalone mode, hidden in addressOnlyMode, savedContactsOnly, or useOnceOnly */}
                {standaloneMode && !addressOnlyMode && !savedContactsOnly && !useOnceOnly && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('contacts.saveMode.label', 'What would you like to do?')}
                    </Typography>
                    <RadioGroup value={saveMode} onChange={e => setSaveMode(e.target.value)} sx={{ mt: 1 }}>
                      <FormControlLabel
                        value="useOnce"
                        control={<Radio />}
                        label={
                          <Typography variant="body2">
                            {t('contacts.saveMode.useOnceLabel', 'Use for this order only')}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t('contacts.saveMode.useOnceHint', 'Changes will not be saved to the address book')}
                            </Typography>
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="save"
                        control={<Radio />}
                        label={
                          <Typography variant="body2">
                            {t('contacts.saveMode.save', 'Save to Address Book')}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t('contacts.saveMode.saveHint', 'Save changes for future use')}
                            </Typography>
                          </Typography>
                        }
                      />
                    </RadioGroup>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

                {/* Form Fields */}
                {renderFormContent(formikProps)}

                {/* Save Location Selector - Only show for regular create mode in non-standalone */}
                {!isEditMode && !isManualEntry && !standaloneMode && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        {t('contacts.savePrompt.saveToLevel', 'Save contact to:')}
                      </Typography>
                      <RadioGroup
                        value={saveLevel}
                        onChange={e => setSaveLevel(e.target.value)}
                        sx={{ mt: 1.5, gap: 1 }}
                      >
                        <FormControlLabel
                          value="site_group"
                          control={<Radio />}
                          label={
                            <Typography variant="body1">
                              <strong>{siteGroupName || t('contacts.savePrompt.siteGroup', 'Site Group')}</strong>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t(
                                  'contacts.savePrompt.siteGroupHint',
                                  'Contact will be available for this site group only',
                                )}
                              </Typography>
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="customer"
                          control={<Radio />}
                          label={
                            <Typography variant="body1">
                              <strong>{customerName || t('contacts.savePrompt.customer', 'Customer')}</strong>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t(
                                  'contacts.savePrompt.customerHint',
                                  'Contact will be available for all site groups under this customer',
                                )}
                              </Typography>
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Box>
                  </>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {/* Back to Address Book / Select Address - left side */}
                {showBackButton && onBackToAddressBook && (
                  <Button onClick={onBackToAddressBook} disabled={formikProps.isSubmitting} sx={{ mr: 'auto' }}>
                    {addressOnlyMode
                      ? t('contacts.backToSelectAddress', '← Back to Select Address')
                      : t('contacts.backToAddressBook', '← Back to Address Book')}
                  </Button>
                )}

                <Box sx={{ display: 'flex', gap: 1, ml: showBackButton && onBackToAddressBook ? 0 : 'auto' }}>
                  {/* Cancel button - hide in standalone mode */}
                  {!standaloneMode && (
                    <Button onClick={onClose} disabled={formikProps.isSubmitting}>
                      {t('contacts.management.cancel', 'Cancel')}
                    </Button>
                  )}

                  {/* Standalone Mode Buttons - based on saveMode selection */}
                  {standaloneMode && (
                    <>
                      {/* useOnceOnly mode: only show "Use Once" button (for editing inline data without FK) */}
                      {useOnceOnly && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'useOnce')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="contained"
                        >
                          {t('contacts.savePrompt.useOnceOnly', 'Use Once Only')}
                        </Button>
                      )}
                      {/* Use Once mode: single button - "Use This Address" for addressOnlyMode, "Use Once Only" otherwise */}
                      {!useOnceOnly && saveMode === 'useOnce' && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'useOnce')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="contained"
                        >
                          {addressOnlyMode
                            ? t('contacts.savePrompt.useThisAddress', 'Use This Address')
                            : t('contacts.savePrompt.useOnceOnly', 'Use Once Only')}
                        </Button>
                      )}
                      {/* Save mode without existing contact: "Save as New Contact" */}
                      {!useOnceOnly && saveMode === 'save' && !hasExistingContact && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'saveAsNew')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="contained"
                        >
                          {t('contacts.savePrompt.saveNew', 'Save as New Contact')}
                        </Button>
                      )}
                      {/* Save mode with existing contact: "Save as New" and "Update" */}
                      {!useOnceOnly && saveMode === 'save' && hasExistingContact && (
                        <>
                          <Button
                            onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'saveAsNew')}
                            disabled={formikProps.isSubmitting || !formikProps.isValid}
                            variant="outlined"
                          >
                            {t('contacts.savePrompt.saveNew', 'Save as New Contact')}
                          </Button>
                          <Button
                            onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'update')}
                            disabled={formikProps.isSubmitting || !formikProps.isValid}
                            variant="contained"
                          >
                            {t('contacts.management.update', 'Update')}
                          </Button>
                        </>
                      )}
                    </>
                  )}

                  {/* Manual Entry Mode: (Cancel, Save, Use Once) - Use Once only if allowUseOnce */}
                  {!standaloneMode && isManualEntry && (
                    <>
                      <Button
                        onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'save')}
                        disabled={formikProps.isSubmitting || !formikProps.isValid}
                        variant={allowUseOnce ? 'outlined' : 'contained'}
                      >
                        {t('contacts.management.save', 'Save')}
                      </Button>
                      {allowUseOnce && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'useOnce')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="contained"
                        >
                          {t('contacts.savePrompt.useOnce', 'Use Once')}
                        </Button>
                      )}
                    </>
                  )}

                  {/* Edit Mode: (Cancel, Update) - Use Once and Save as New only if allowUseOnce */}
                  {/* Note: isManualEntry takes precedence - if user clicked "Enter Manually", treat as manual entry not edit */}
                  {!standaloneMode && isEditMode && !isManualEntry && (
                    <>
                      {allowUseOnce && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'useOnce')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="outlined"
                        >
                          {t('contacts.savePrompt.useOnce', 'Use Once')}
                        </Button>
                      )}
                      {allowUseOnce && (
                        <Button
                          onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'saveAsNew')}
                          disabled={formikProps.isSubmitting || !formikProps.isValid}
                          variant="outlined"
                        >
                          {t('contacts.savePrompt.saveAsNew', 'Save as New')}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleSubmitWithValidation(formikProps.values, formikProps, 'update')}
                        disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                        variant="contained"
                      >
                        {t('contacts.management.update', 'Update')}
                      </Button>
                    </>
                  )}

                  {/* Regular Create Mode: (Cancel, Create) */}
                  {!standaloneMode && !isManualEntry && !isEditMode && (
                    <Button
                      type="submit"
                      disabled={formikProps.isSubmitting || !formikProps.isValid}
                      variant="contained"
                    >
                      {t('contacts.management.create', 'Create')}
                    </Button>
                  )}
                </Box>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount={true}
        enableReinitialize={true}
      >
        {formikProps => (
          <Form>
            <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
            {renderFormContent(formikProps)}
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={onClose} disabled={formikProps.isSubmitting}>
                {t('contacts.management.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={formikProps.isSubmitting}>
                {isEditMode ? t('contacts.management.update', 'Update') : t('contacts.management.create', 'Create')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

ContactForm.propTypes = {
  /** Whether the dialog is open (required even in inline mode) */
  open: PropTypes.bool.isRequired,
  /** Function to call when closing the form */
  onClose: PropTypes.func.isRequired,
  /** Contact object for edit mode (null for create mode) */
  contact: PropTypes.object,
  /** Customer ID - optional for use-once scenarios where contact won't be saved */
  customerId: PropTypes.number,
  /** Site Group ID (optional) */
  siteGroupId: PropTypes.number,
  /** Callback function called with saved contact on success */
  onSuccess: PropTypes.func,
  /** Display mode: 'dialog' (default) or 'inline' */
  mode: PropTypes.oneOf(['dialog', 'inline']),
  /** Show "Back to Address Book" button */
  showBackButton: PropTypes.bool,
  /** Callback for back to address book button */
  onBackToAddressBook: PropTypes.func,
  /** Flag for manual entry mode */
  isManualEntry: PropTypes.bool,
  /** Customer name for save location selector */
  customerName: PropTypes.string,
  /** Site Group name for save location selector */
  siteGroupName: PropTypes.string,
  /** @deprecated - now derived from contact prop. Whether there's an existing contact FK relationship */
  hasExistingContact: PropTypes.bool,
  /** Enable standalone mode with all action buttons */
  standaloneMode: PropTypes.bool,
  /** Allow "Use Once" button in non-standalone modes (for AddressBookDialog context) */
  allowUseOnce: PropTypes.bool,
  /** Array of required fields: 'email', 'name', 'address', 'phone'. Empty array = all fields optional. */
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  /** Context for primary options: 'all' shows both RMA and Sales, 'rma' or 'sales' shows only that context, 'none' hides both */
  primaryContext: PropTypes.oneOf(['all', 'rma', 'sales', 'none']),
  /** @deprecated - now derived from contact prop. When true, only allow "Use Once" */
  useOnceOnly: PropTypes.bool,
  /** When true, hide save options and only allow "Use for this order only" */
  addressOnlyMode: PropTypes.bool,
  /** When true, contacts must be saved to address book (hide "Use for this order only" option) */
  savedContactsOnly: PropTypes.bool,
  /** When true, if ANY field is filled, require name/businessName, email, and full address */
  validateIfAnyFilled: PropTypes.bool,
};

export default ContactForm;
